import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
// FIXME circular dependency
import { getFieldType } from '@databases/fields';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import InlineEditWrapper from '@databases/inline-edit/InlineEditWrapper';
import { messages } from '@databases/intl/messages';
import { isApple } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { type Adjacents } from '@databases/selections-api-types';
import { getService } from '@databases/service-container';
import { SkeletonItem } from '@databases/skeleton';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import FieldNameContainer from './FieldNameContainer.ts';
import { FieldNameViewContainer } from './FieldNameViewContainer.ts';

const StyledInlineEditWrapper = styled(InlineEditWrapper)`
  > ${FieldNameViewContainer} {
    padding: ${token('space.025')} ${token('space.050', '4px')}
      ${token('space.025', '2px')} ${token('space.050', '4px')};
  }
`;

export interface FieldNameHeaderProps {
  contentId: string;
  field: DatabaseField<DatabaseFieldType>;
  hoverDeleteButton?: boolean;
  isReadonly?: boolean;
  isSelected?: boolean;
  selectedAdjacentCells?: Adjacents;
  showIcon?: boolean;
  actionComp?: React.ReactNode;
  inlineActionComp?: React.ReactNode;
  onFieldSettingsPanelOpen?: () => void;
  openSettingsPanelOnClick?: boolean;
}

const fieldNameViewButtonContainerStyles = xcss({
  whiteSpace: 'pre',
  overflow: 'hidden',
  display: 'block',
  ':focus-within': {
    overflow: 'visible'
  }
});
const FieldNameHeader = forwardRef<HTMLInputElement, FieldNameHeaderProps>(
  function FieldNameHeader(
    {
      contentId,
      field,
      showIcon,
      isSelected,
      hoverDeleteButton,
      selectedAdjacentCells,
      isReadonly,
      actionComp,
      inlineActionComp,
      onFieldSettingsPanelOpen,
      openSettingsPanelOnClick = true
    },
    _ref
  ) {
    const { showPanel } = usePanel();
    const { closeMenu } = useContextualMenu();
    const selectionsApi = getService('SelectionsApi');
    const { formatMessage } = useIntl();
    const client = useAnalytics();

    /**
     * Modifier behavior must match table header selection.
     *
     * - Click selects the field and opens its settings.
     * - Shift extends the range.
     * - Shift+Meta/Ctrl toggles an individual field.
     */
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (isReadonly) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const isMultiSelectOn = isModifierKeyPressed(e, [
          isApple ? 'metaKey' : 'ctrlKey',
          'shiftKey'
        ]);
        const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);

        if (isRangeSelectOn) {
          blurActiveElement();
          removeTextSelection();

          selectionsApi.applyFieldSelection({
            fieldId: field.id,
            gesture: isMultiSelectOn ? 'toggle' : 'extend'
          });
          selectionsApi.clearPreviewIntent();

          return;
        }

        selectionsApi.applyFieldSelection({ fieldId: field.id, gesture: 'replace' });

        if (!openSettingsPanelOnClick) {
          selectionsApi.clearPreviewIntent();
          return;
        }

        onFieldSettingsPanelOpen?.();
        showPanel('FieldSettingsPanel', { fieldId: field.id });
        closeMenu();
        client.sendDatabaseFieldSettingsOpenedEvent({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'fieldSettingsPanel',
          fieldType: field.type
        });
      },
      [
        isReadonly,
        selectionsApi,
        contentId,
        field.id,
        showPanel,
        closeMenu,
        client,
        onFieldSettingsPanelOpen,
        openSettingsPanelOnClick,
        field.type
      ]
    );

    const hasNonEmptyFieldName = useMemo(
      () => field.name && field.name !== '',
      [field.name]
    );

    return (
      <FieldNameContainer onClick={handleClick} data-testid="field-name-container">
        {showIcon && (
          <SkeletonItem isButton>
            <FieldTypeIcon
              type={field.type}
              label={getFieldType(field.type).label}
              noTooltip
              jiraField={field.settings.jiraField}
              showSyncMessage
            />
          </SkeletonItem>
        )}
        {/* TODO: we should bring selection related styles into this component to avoid using the inline edit wrapper */}
        <StyledInlineEditWrapper
          hideBorderOnFocus
          isDraggable={false}
          isSelected={isSelected}
          markAsDeleted={hoverDeleteButton}
          selectedAdjacentCells={selectedAdjacentCells}
          ariaLabel={field.name}
        >
          <Flex direction="row" justifyContent="space-between" alignItems="center">
            <Inline alignBlock="center" space="space.025">
              <SkeletonItem minWidth={80}>
                <FieldNameViewContainer>
                  {hasNonEmptyFieldName
                    ? field.name
                    : formatMessage(messages['field.default-field-name'])}
                </FieldNameViewContainer>
              </SkeletonItem>
              {inlineActionComp && (
                <Box xcss={xcss({ flexShrink: 0, marginInlineStart: 'space.050' })}>
                  {inlineActionComp}
                </Box>
              )}
            </Inline>
            <Box xcss={fieldNameViewButtonContainerStyles}>
              {actionComp && <SkeletonItem minWidth={80}>{actionComp}</SkeletonItem>}
            </Box>
          </Flex>
        </StyledInlineEditWrapper>
      </FieldNameContainer>
    );
  }
);

export default FieldNameHeader;
